import { View } from "react-native";
import { ActivityCard } from "./ActivityCard";
import { ReadingsList } from "./ActivityCard/ReadingsList";
import { SectionHeading } from "./SectionHeading";
import { ProgressBar } from "@md-design-system/ProgressBar";
import { useNavigation } from '@react-navigation/native';
import { Head } from "@shared/components/head";
import { Text } from "@shared/components/text";
import { dynamicTranslations } from "@shared/utils/dynamicTranslation";
import { ActivityType, IPathwayActivitiesResponse, IPathwayActivity } from "app/_modules/pathways/entities";
import { ACTIVITY_STATUS } from "../../constants";
import { ActivityName } from "../../types";
import { ACTIVITY_CARD_STATUS } from "../../constants/activityCardStatus";
import { TActivityBucket, mapActivityToCardStatus, hasRepeatRequest, compareTodoOrder } from "../../utils/activityCard";
import { Telemetry } from "app/services/telemetry/Telemetry";
import { TOUR_ANCHORS, TourAnchor, TourAnchorItem } from "app/_modules/onboarding";
import type { TTourAnchor } from "app/_modules/onboarding";

interface ActivitiesSectionProps {
    activities: IPathwayActivitiesResponse;
}

type TBucketedActivity = { activity: IPathwayActivity; bucket: TActivityBucket };

const isAppointment = (activity: IPathwayActivity) =>
    activity.activityType === ActivityType.PATHWAY_APPOINTMENT;

const ActivitiesSection = ({ activities }: ActivitiesSectionProps) => {
    const { t } = dynamicTranslations();
    const navigation = useNavigation<any>();

    const handleActivityNavigation = (activityName: string, activityId: string, activityType: string, bucket: TActivityBucket) => {
        Telemetry.event('ui.home.activity_started', {
            domain: 'ui',
            data: { activityName, activityId, activityType },
        });

        switch (activityType) {
            case ActivityType.PATHWAY_VITAL_PARAMETER:
                navigation.navigate('MeasurementFlow', { activityName, activityId });
                break;
            case ActivityType.PATHWAY_DRUG:
                navigation.navigate('DrugIntakeFlow', { activityName, activityId, bucket });
                break;
            case ActivityType.PATHWAY_QUESTIONNAIRE:
                navigation.navigate('QuestionnaireFlow', { activityName, activityId });
                break;
        }
    };

    const buckets: TActivityBucket[] = ['daily', 'morning', 'afternoon', 'evening'];
    const allActivities: TBucketedActivity[] = buckets.flatMap((bucket) =>
        (activities[bucket] || [])
            .filter((activity) => !isAppointment(activity))
            .map((activity) => ({ activity, bucket }))
    );

    const queued = allActivities.filter(({ activity }) => activity.activityName !== ActivityName.STEP);

    const todo: TBucketedActivity[] = [];
    const later: TBucketedActivity[] = [];
    const completed: TBucketedActivity[] = [];
    const notCompleted: TBucketedActivity[] = [];
    const addedByYou: TBucketedActivity[] = [];

    queued.forEach((item) => {
        if (hasRepeatRequest(item.activity)) {
            todo.push(item);
            return;
        }
        const cardStatus = mapActivityToCardStatus(item.activity, item.bucket);
        switch (cardStatus) {
            case ACTIVITY_CARD_STATUS.TODO:
                todo.push(item);
                break;
            case ACTIVITY_CARD_STATUS.LOCKED:
                later.push(item);
                break;
            case ACTIVITY_CARD_STATUS.EXTRA:
                addedByYou.push(item);
                break;
            case ACTIVITY_CARD_STATUS.NOT_COMPLETED:
                notCompleted.push(item);
                break;
            default:
                completed.push(item);
        }
    });

    todo.sort(compareTodoOrder);

    const totalCount = todo.length + later.length + completed.length + notCompleted.length + addedByYou.length;
    const doneCount = completed.filter(({ activity }) => activity.status === ACTIVITY_STATUS.COMPLETED).length + addedByYou.length;
    const progress = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

    const tourActivityId = todo.find(
        ({ activity }) => !hasRepeatRequest(activity),
    )?.activity.activityId;

    const renderCard = ({ activity, bucket }: TBucketedActivity, disabled = false) => (
        <ActivityCard
            key={activity.activityId}
            activity={activity}
            bucket={bucket}
            disabled={disabled}
            ctaAnchor={
                activity.activityId === tourActivityId ? TOUR_ANCHORS.homeActivityCta : undefined
            }
            onPress={() => handleActivityNavigation(activity.activityName, activity.activityId, activity.activityType, bucket)}
        />
    );

    const renderCardGroup = (
        title: string,
        items: TBucketedActivity[],
        count?: string,
        anchor?: TTourAnchor,
    ) =>
        items.length > 0 && (
            <TourAnchor anchor={anchor}>
                <View className="flex-col gap-4">
                    <SectionHeading title={title} count={count} />
                    <View className="flex-col gap-3">
                        {items.map((item) => (
                            <TourAnchorItem key={item.activity.activityId}>
                                {renderCard(item, true)}
                            </TourAnchorItem>
                        ))}
                    </View>
                </View>
            </TourAnchor>
        );

    const renderLogGroup = (title: string, items: TBucketedActivity[], anchor?: TTourAnchor) =>
        items.length > 0 && (
            <TourAnchor anchor={anchor}>
                <View className="flex-col gap-4">
                    <SectionHeading title={title} count={String(items.length)} />
                    <ReadingsList>
                        {items.map((item) => (
                            <TourAnchorItem key={item.activity.activityId}>
                                {renderCard(item, true)}
                            </TourAnchorItem>
                        ))}
                    </ReadingsList>
                </View>
            </TourAnchor>
        );

    return (
        <View className="flex-col gap-8">
            <View className="mt-3">
                <TourAnchor anchor={TOUR_ANCHORS.homeProgress}>
                    <View className="flex-col gap-4">
                        <View className="flex-col gap-3">
                            <View className="flex-row items-end justify-between">
                                <Head type="h3">{t('screens.activities.section.todayTodo')}</Head>
                                <Text className="text-gray-500 text-base">
                                    <Text className="font-bold text-gray-500">{doneCount} {t('screens.activities.section.of')} {totalCount}</Text> {t('screens.activities.section.completedWord')}
                                </Text>
                            </View>
                            <ProgressBar percentage={progress} complete={totalCount > 0 && doneCount >= totalCount} />
                        </View>

                        <View className="flex-col gap-3">
                            {todo.map((item) => (
                                <TourAnchorItem key={item.activity.activityId}>
                                    {item.activity.activityId === tourActivityId ? (
                                        <TourAnchor anchor={TOUR_ANCHORS.homeActivityCard}>
                                            {renderCard(item)}
                                        </TourAnchor>
                                    ) : (
                                        renderCard(item)
                                    )}
                                </TourAnchorItem>
                            ))}
                        </View>
                    </View>
                </TourAnchor>
            </View>

            {renderCardGroup(
                t('screens.activities.section.later'),
                later,
                t('screens.activities.section.waiting', { n: later.length }),
                TOUR_ANCHORS.homeSectionLater,
            )}
            {renderLogGroup(
                t('screens.activities.section.completed'),
                [...completed, ...addedByYou],
                TOUR_ANCHORS.homeSectionCompleted,
            )}
            {renderLogGroup(t('screens.activities.section.notCompleted'), notCompleted)}
        </View>
    );
};

export default ActivitiesSection;
